/**
 * ContourService — Client-side contour generation from elevation points.
 * IDW grid + simplified isoline extraction → GeoJSON LineString features.
 */
import type { Feature, FeatureCollection, LineString } from 'geojson';

export interface ContourOptions {
  zField?: string;
  interval?: number;
  gridSize?: number;
}

function pickZ(props: Record<string, unknown> | null | undefined, zField: string): number | null {
  if (!props) return null;
  const candidates = [zField, 'elevation', 'elev', 'z', 'Z', 'height', 'HEIGHT', 'altitude'];
  for (const key of candidates) {
    const v = props[key];
    if (typeof v === 'number' && isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '' && isFinite(Number(v))) return Number(v);
  }
  return null;
}

export class ContourService {
  /**
   * Generate contour LineString features from Point features with Z.
   */
  async generateFromPoints(
    features: Array<{ geometry?: GeoJSON.Geometry; properties?: Record<string, unknown> }>,
    zField: string = 'elevation',
    interval: number = 5
  ): Promise<Feature<LineString>[]> {
    const pts: Array<{ x: number; y: number; z: number }> = [];

    for (const f of features) {
      if (!f.geometry || f.geometry.type !== 'Point') continue;
      const [x, y] = f.geometry.coordinates as [number, number];
      // Point Z from coordinates[2] or properties
      let z: number | null = null;
      if (f.geometry.coordinates.length >= 3 && isFinite(f.geometry.coordinates[2] as number)) {
        z = f.geometry.coordinates[2] as number;
      } else {
        z = pickZ(f.properties, zField);
      }
      if (z == null || !isFinite(x) || !isFinite(y)) continue;
      pts.push({ x, y, z });
    }

    if (pts.length < 4) return [];

    const minX = Math.min(...pts.map((p) => p.x));
    const maxX = Math.max(...pts.map((p) => p.x));
    const minY = Math.min(...pts.map((p) => p.y));
    const maxY = Math.max(...pts.map((p) => p.y));
    const minZ = Math.min(...pts.map((p) => p.z));
    const maxZ = Math.max(...pts.map((p) => p.z));

    const span = Math.max(maxX - minX, maxY - minY, 1e-9);
    const cell = span / 48;
    const nx = Math.max(8, Math.ceil((maxX - minX) / cell) + 1);
    const ny = Math.max(8, Math.ceil((maxY - minY) / cell) + 1);

    // IDW grid
    const grid: number[][] = [];
    for (let iy = 0; iy < ny; iy++) {
      const row: number[] = [];
      for (let ix = 0; ix < nx; ix++) {
        const gx = minX + ix * cell;
        const gy = minY + iy * cell;
        row.push(this.idw(gx, gy, pts));
      }
      grid.push(row);
    }

    const contours: Feature<LineString>[] = [];
    const startZ = Math.ceil(minZ / interval) * interval;

    for (let z = startZ; z <= maxZ + 1e-9; z += interval) {
      const segments = this.marchingSquares(grid, minX, minY, cell, z);
      // Merge short segments into polylines (greedy)
      const lines = this.mergeSegments(segments);
      for (const coords of lines) {
        if (coords.length < 2) continue;
        contours.push({
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: coords },
          properties: { elevation: Math.round(z * 1000) / 1000, interval, source: 'contour' },
        });
      }
    }

    return contours;
  }

  /** Marching squares — extract isoline segments at value z */
  private marchingSquares(
    grid: number[][],
    minX: number,
    minY: number,
    cell: number,
    z: number
  ): Array<[[number, number], [number, number]]> {
    const segs: Array<[[number, number], [number, number]]> = [];
    const ny = grid.length;
    const nx = grid[0]?.length || 0;

    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

    for (let iy = 0; iy < ny - 1; iy++) {
      for (let ix = 0; ix < nx - 1; ix++) {
        const v00 = grid[iy][ix];
        const v10 = grid[iy][ix + 1];
        const v01 = grid[iy + 1][ix];
        const v11 = grid[iy + 1][ix + 1];

        const x0 = minX + ix * cell;
        const x1 = minX + (ix + 1) * cell;
        const y0 = minY + iy * cell;
        const y1 = minY + (iy + 1) * cell;

        // Binary case 0-15
        let caseIndex = 0;
        if (v00 >= z) caseIndex |= 1;
        if (v10 >= z) caseIndex |= 2;
        if (v11 >= z) caseIndex |= 4;
        if (v01 >= z) caseIndex |= 8;
        if (caseIndex === 0 || caseIndex === 15) continue;

        const edge = (side: 'top' | 'right' | 'bottom' | 'left'): [number, number] => {
          if (side === 'top') {
            const t = (z - v00) / (v10 - v00 || 1e-12);
            return [lerp(x0, x1, t), y0];
          }
          if (side === 'right') {
            const t = (z - v10) / (v11 - v10 || 1e-12);
            return [x1, lerp(y0, y1, t)];
          }
          if (side === 'bottom') {
            const t = (z - v01) / (v11 - v01 || 1e-12);
            return [lerp(x0, x1, t), y1];
          }
          const t = (z - v00) / (v01 - v00 || 1e-12);
          return [x0, lerp(y0, y1, t)];
        };

        // Simplified edge pairs for common cases
        const pairs: Array<[string, string]> = [];
        switch (caseIndex) {
          case 1:
          case 14:
            pairs.push(['left', 'top']);
            break;
          case 2:
          case 13:
            pairs.push(['top', 'right']);
            break;
          case 3:
          case 12:
            pairs.push(['left', 'right']);
            break;
          case 4:
          case 11:
            pairs.push(['right', 'bottom']);
            break;
          case 5:
            pairs.push(['left', 'top'], ['right', 'bottom']);
            break;
          case 6:
          case 9:
            pairs.push(['top', 'bottom']);
            break;
          case 7:
          case 8:
            pairs.push(['left', 'bottom']);
            break;
          case 10:
            pairs.push(['top', 'right'], ['left', 'bottom']);
            break;
          default:
            break;
        }
        for (const [a, b] of pairs) {
          segs.push([
            edge(a as 'top' | 'right' | 'bottom' | 'left'),
            edge(b as 'top' | 'right' | 'bottom' | 'left'),
          ]);
        }
      }
    }
    return segs;
  }

  private mergeSegments(segs: Array<[[number, number], [number, number]]>): number[][][] {
    if (segs.length === 0) return [];
    const eps = 1e-9;
    const key = (p: [number, number]) => `${p[0].toFixed(8)},${p[1].toFixed(8)}`;
    const used = new Set<number>();
    const lines: number[][][] = [];

    const near = (a: [number, number], b: [number, number]) =>
      Math.hypot(a[0] - b[0], a[1] - b[1]) < eps * 1000 + 1e-10;

    for (let i = 0; i < segs.length; i++) {
      if (used.has(i)) continue;
      used.add(i);
      let line: [number, number][] = [segs[i][0], segs[i][1]];
      let extended = true;
      while (extended) {
        extended = false;
        for (let j = 0; j < segs.length; j++) {
          if (used.has(j)) continue;
          const [a, b] = segs[j];
          const head = line[0];
          const tail = line[line.length - 1];
          if (near(tail, a)) {
            line.push(b);
            used.add(j);
            extended = true;
          } else if (near(tail, b)) {
            line.push(a);
            used.add(j);
            extended = true;
          } else if (near(head, a)) {
            line.unshift(b);
            used.add(j);
            extended = true;
          } else if (near(head, b)) {
            line.unshift(a);
            used.add(j);
            extended = true;
          }
        }
      }
      lines.push(line);
    }
    return lines;
  }

  private idw(
    x: number,
    y: number,
    pts: Array<{ x: number; y: number; z: number }>,
    power = 2
  ): number {
    let num = 0;
    let den = 0;
    for (const p of pts) {
      const d = Math.hypot(p.x - x, p.y - y);
      if (d < 1e-12) return p.z;
      const w = 1 / Math.pow(d, power);
      num += w * p.z;
      den += w;
    }
    return den > 0 ? num / den : 0;
  }

  toFeatureCollection(features: Feature<LineString>[]): FeatureCollection {
    return { type: 'FeatureCollection', features };
  }
}

export const contourService = new ContourService();
